import React, { useState, useEffect, useRef, useContext } from 'react';
import { connect } from 'react-redux';
import { Drawer, Tabs, Form, Select, Input, Space, Button, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import * as BillApi from '@/api/bill';
import * as CreditApi from '@/api/bill/credit';
import { toggleCreditDrawerVisible, initAllOta } from '@/redux/actions/bill/credit';
import { initConsumeSubjects, initPaymentSubjects } from '@/redux/actions/bill';

import Payment from '@/components/Payment';
import PayModal from '@/components/Payment/payModal';
import { PayResult, PayModalRef, PayWay } from '@/components/Payment/interface';
import { channelOrderNoValidater } from '@/pages/reservation/utils/validator';
import { parse2Number, parseFen2Currency, parseYuan2Fen } from '@/utils/number';
import IntlContext from '@/components/Intl/IntlContext';

import { validateAmount, formatAmount } from '../util/helper';
import {
  OTA_SUBJECT_CODE,
  MT_ZL,
  XC_ZL,
  MT_SUBJECTS,
  XC_SUBJECTS,
  DY_SUBJECTS,
  DY_ZL,
} from '../config';
import commonStyles from '../common.module.scss';
import styles from './index.module.scss';

const { TabPane } = Tabs;
const { TextArea } = Input;

function Credit(props) {
  const { resvNo, paymentSubjects, consumeSubjects, selectedRoom, drawerVisible, allOta } = props;

  const [payForm] = Form.useForm();
  const [consumeForm] = Form.useForm();
  const [otaForm] = Form.useForm();
  const payModalRef = useRef<PayModalRef>();

  // Payment & PayModal props
  const [initPayTypes, setInitPayTypes] = useState(false);
  const [payType, setPayType] = useState(3);
  const [certNo, setCertNo] = useState('');
  const [wyuid, setWyuid] = useState('');

  // self states
  const [activeKey, setActiveKey] = useState('1'); // 1: 付款, 2:消费
  const [payLoading, setPayLoading] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);
  const [amount, setAmount] = useState(null); // 入账金额
  const [isOta, setIsOta] = useState(false); // 酒店OTA房费预付
  const [isOtaDirect, setIsOtaDirect] = useState(false); // OTA直连
  const [otaSubjects, setOtaSubjects] = useState(null);
  const intlCtx = useContext(IntlContext);

  const payResultCallback = (payResult: PayResult) => {
    if (payResult.generatePayOrder) {
      props.toggleCreditDrawerVisible(false);
      return;
    }
    if (payResult.payed && payResult.result) {
      message.success(intl.get('bill_credit_pay_account_success'));
      props.toggleCreditDrawerVisible(false);
      // parent component callback
      if (props.reloadList && typeof props.reloadList === 'function') props.reloadList();
    }

    if (payResult.payed && !payResult.result) {
      message.error(intl.get('bill_credit_pay_account_fail'));
    }
  };

  const resetPayModalProps = () => {
    setInitPayTypes(false);
    setCertNo('');
  };

  const setDefaultSubject = (otaFlag, channelName) => {
    // OTA 直连特殊处理
    if (otaFlag) {
      // dynamic default ota subject
      if (channelName === MT_ZL) {
        consumeForm.setFieldsValue({
          subjectCode: MT_SUBJECTS[0].value,
        });
      }
      if (channelName === XC_ZL) {
        consumeForm.setFieldsValue({
          subjectCode: XC_SUBJECTS[0].value,
        });
      }
      if (channelName === DY_ZL) {
        consumeForm.setFieldsValue({
          subjectCode: DY_SUBJECTS[0].value,
        });
      }
    } else {
      payForm.setFieldsValue({
        subjectCode: paymentSubjects.length > 0 ? paymentSubjects[0].value : '',
      });
      consumeForm.setFieldsValue({
        subjectCode: consumeSubjects.length > 0 ? consumeSubjects[0].value : '',
      });
    }
  };

  const changeVisible = (visible) => {
    if (visible) {
      // OTA 直连特殊处理  status: 0-暂存 1-预订中 2-预订取消 3-入住中 4-已退房
      const otaDirectFlag =
        selectedRoom.status === 2 &&
        (selectedRoom.priceChannel === MT_ZL ||
          selectedRoom.priceChannel === XC_ZL ||
          selectedRoom.priceChannel === DY_ZL);
      setIsOtaDirect(otaDirectFlag);

      if (otaDirectFlag) {
        setActiveKey('2');
        // dynamic ota subjects
        if (selectedRoom.priceChannel === MT_ZL) {
          setOtaSubjects(MT_SUBJECTS);
        }
        if (selectedRoom.priceChannel === XC_ZL) {
          setOtaSubjects(XC_SUBJECTS);
        }
        if (selectedRoom.priceChannel === DY_ZL) {
          setOtaSubjects(DY_SUBJECTS);
        }
      } else {
        setInitPayTypes(true);
      }
      setDefaultSubject(otaDirectFlag, selectedRoom.priceChannel);
    } else {
      // rest states & payModal props
      payForm.resetFields();
      consumeForm.resetFields();
      otaForm.resetFields();
      setAmount(null);
      setIsOta(false);
      setIsOtaDirect(false);
      setOtaSubjects(null);
      resetPayModalProps();
    }
  };

  const closeDrawer = () => {
    props.toggleCreditDrawerVisible(false);
    payForm.resetFields();
    consumeForm.resetFields();
    otaForm.resetFields();
    setAmount(null);
    resetPayModalProps();
  };

  const generateCreditOrder = (formValue) => {
    const params = {
      fkdFlag: payType === PayWay.Fukuandan,
      ...formValue,
      roomId: selectedRoom.roomId,
      roomNo: selectedRoom.roomNo,
      recNo: selectedRoom.recNo,
      subjectAmount: parse2Number(parseYuan2Fen(formValue.subjectAmount)),
      resvNo,
    };
    return BillApi.generateBizOrder(params);
  };

  const gotoPay = async (confirm?: boolean) => {
    let values;
    try {
      values = await payForm.validateFields();
    } catch (err) {
      message.warning(intl.get('bill_credit_check_input'));
      return;
    }

    if (isOta) {
      // let otaFormValues;

      // try {
      const otaFormValues = await otaForm.validateFields();
      // } catch (err) {
      //   // message.warning(intl.get('bill_credit_input_channel_bill_no'));
      //   return;
      // }

      try {
        // 付款单需confirm后下单
        if (!confirm && payType === PayWay.Fukuandan) {
          payModalRef.current.openModal({
            payType,
            showFukuanDanConfirm: true,
          });
          return;
        }

        setPayLoading(true);
        const res = await CreditApi.creditOtaAmount({
          ...otaFormValues,
          roomId: selectedRoom.roomId,
          roomNo: selectedRoom.roomNo,
          recNo: selectedRoom.recNo,
          amount: parse2Number(parseYuan2Fen(values.subjectAmount)),
          remark: values.remark,
          resvNo,
        });

        if (res && res.status) {
          message.success(intl.get('bill_credit_ota_pay_account_success'));
          props.toggleCreditDrawerVisible(false);
          // parent component callback
          if (props.reloadList && typeof props.reloadList === 'function') props.reloadList();
        } else {
          message.error(res.msg || intl.get('bill_credit_ota_pay_account_fail'));
        }
        setPayLoading(false);
      } catch (err) {
        setPayLoading(false);
      }
    } else {
      try {
        // 付款单需confirm后下单
        if (!confirm && payType === PayWay.Fukuandan) {
          payModalRef.current.openModal({
            payType,
            showFukuanDanConfirm: true,
          });
          return;
        }

        setPayLoading(true);
        const res = await generateCreditOrder(values);

        if (res && res.status) {
          payModalRef.current.openModal({
            payAmount: amount,
            payType,
            payOrderType: 102,
            paymentNo: res.data?.paymentNo,
            certNo,
            wyuid,
            extraParams: {
              trigger: '执行入账，产生付款账目时',
              operationPage: '入账抽屉',
            },
          });
        } else {
          message.error(res.msg || intl.get('bill_credit_genarate_bill_fail'));
        }
        setPayLoading(false);
      } catch (err) {
        setPayLoading(false);
      }
    }
  };

  const credit = async () => {
    if (!selectedRoom.recNo) {
      message.warning(intl.get('bill_credit_can_not_account_msg'));
      return;
    }

    let values;
    try {
      values = await consumeForm.validateFields();
    } catch (err) {
      message.warning(intl.get('bill_credit_input_consume_amount'));
    }

    try {
      setCreditLoading(true);

      const creditParams = {
        ...values,
        roomId: selectedRoom.roomId,
        roomNo: selectedRoom.roomNo,
        recNo: selectedRoom.recNo,
        amount: parse2Number(parseYuan2Fen(values.amount)),
        resvNo,
      };
      const res = await CreditApi.creditConsume(creditParams);

      if (res && res.status) {
        message.success(intl.get('bill_credit_consume_success'));
        props.toggleCreditDrawerVisible(false);

        // parent component callback
        if (props.reloadList && typeof props.reloadList === 'function') props.reloadList();
      } else {
        message.error(res.msg || intl.get('bill_credit_consume_fail'));
      }
      setCreditLoading(false);
    } catch (err) {
      setCreditLoading(false);
    }
  };

  const changePaySubject = (code) => {
    if (code === OTA_SUBJECT_CODE) {
      setIsOta(true);
      otaForm.setFieldsValue({
        otaChannel: allOta.length > 0 ? allOta[0].value : '',
      });
    } else {
      setIsOta(false);
    }
  };

  const changeAmount = async () => {
    try {
      const values = await payForm.validateFields();
      setAmount(parse2Number(parseYuan2Fen(values.subjectAmount)));
    } catch (err) {
      setAmount(null);
    }
  };

  useEffect(() => {
    props.initConsumeSubjects();
    props.initPaymentSubjects();
    props.initAllOta();
    return () => {
      // rest states & payModal props when unmounted
      payForm.resetFields();
      consumeForm.resetFields();
      otaForm.resetFields();
      setAmount(null);
      resetPayModalProps();
    };
  }, []);

  const drawerFooter = () => {
    // OTA直连
    if (isOtaDirect) {
      return (
        <Space size={16} style={{ marginLeft: 8 }}>
          <Button type="primary" loading={creditLoading} onClick={() => credit()}>
            {intl.get('bill_credit_account')}
          </Button>
          <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        {activeKey === '1' ? (
          <Button type="primary" loading={payLoading} onClick={() => gotoPay()}>
            {intl.get('bill_credit_collection')}
          </Button>
        ) : (
          <Button type="primary" loading={creditLoading} onClick={() => credit()}>
            {intl.get('bill_credit_account')}
          </Button>
        )}
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  const renderTabContent = () => {
    // OTA直连
    if (isOtaDirect) {
      return (
        <TabPane tab={intl.get('bill_consume')} key="2">
          <dl className={styles['credit-block']}>
            <dt className={commonStyles['bill-block-title']}>
              {intl.get('bill_credit_consume_project')}
            </dt>
            <dd>
              <Form
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 8 }}
                form={consumeForm}
                preserve={false}
                className={styles['credit-form']}
              >
                <Form.Item label={intl.get('bill_refound_res_bill_subject')} name="subjectCode">
                  <Select
                    placeholder={intl.get('bill_credit_please_select')}
                    options={otaSubjects}
                  />
                </Form.Item>
                <Form.Item
                  label={intl.get('bill_credit_amount')}
                  name="amount"
                  rules={[
                    {
                      validator: (_, value) => {
                        return validateAmount(value, {
                          required: true,
                        });
                      },
                    },
                  ]}
                >
                  <Input
                    prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                    placeholder={intl.get('bill_credit_input_consume_amount')}
                    onBlur={(e) => {
                      const v = formatAmount(e.target.value);
                      consumeForm.setFieldsValue({
                        amount: v,
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item label={intl.get('bill_remark')} name="remark">
                  <TextArea
                    showCount
                    rows={4}
                    maxLength={10000}
                    placeholder={intl.get('bill_credit_account_remark_placeholder')}
                  />
                </Form.Item>
              </Form>
            </dd>
          </dl>
        </TabPane>
      );
    }

    return (
      <>
        <TabPane tab={intl.get('bill_refound_res_pay')} key="1" forceRender>
          <dl className={styles['credit-block']}>
            <dt className={commonStyles['bill-block-title']}>
              {intl.get('bill_credit_pay_project')}
            </dt>
            <dd>
              <Form
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 8 }}
                form={payForm}
                preserve={false}
                className={styles['credit-form']}
              >
                <Form.Item label={intl.get('bill_refound_res_bill_subject')} name="subjectCode">
                  <Select
                    placeholder={intl.get('bill_invoice_modal_please_select')}
                    options={paymentSubjects}
                    onChange={(v) => changePaySubject(v)}
                  />
                </Form.Item>
                <Form.Item
                  label={intl.get('bill_credit_amount')}
                  name="subjectAmount"
                  rules={[
                    {
                      validator: (_, value) => {
                        return validateAmount(value, {
                          required: true,
                        });
                      },
                    },
                  ]}
                >
                  <Input
                    prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                    placeholder={intl.get('bill_credit_input_pay_amount')}
                    onChange={() => changeAmount()}
                    onBlur={(e) => {
                      const v = formatAmount(e.target.value);
                      payForm.setFieldsValue({
                        subjectAmount: v,
                      });
                      changeAmount();
                    }}
                  />
                </Form.Item>
                <Form.Item label={intl.get('bill_remark')} name="remark">
                  <TextArea
                    showCount
                    rows={4}
                    maxLength={10000}
                    placeholder={intl.get('bill_credit_account_remark_placeholder')}
                  />
                </Form.Item>
              </Form>
            </dd>
          </dl>
          {isOta ? (
            <dl className={styles['credit-block']} style={{ marginTop: 24 }}>
              <dt className={commonStyles['bill-block-title']}>
                {intl.get('bill_credit_channel_info')}
              </dt>
              <dd>
                <Form
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 8 }}
                  form={otaForm}
                  preserve={false}
                  className={styles['credit-form']}
                >
                  <Form.Item label={intl.get('bill_refound_res_pay_channel')} name="otaChannel">
                    <Select
                      placeholder={intl.get('bill_credit_please_select')}
                      options={allOta}
                      onChange={(val) =>
                        otaForm.setFields([
                          { name: 'otaChannel', value: val },
                          { name: 'channelNo', value: '', errors: [] },
                        ])
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    label={intl.get('bill_refound_res_channel_no')}
                    name="channelNo"
                    rules={[
                      {
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject(
                              new Error(intl.get('bill_credit_input_channel_bill_no')),
                            );
                          }
                          const channelType = otaForm.getFieldValue('otaChannel');
                          const isSuccess = channelOrderNoValidater(channelType, value);
                          if (!isSuccess) {
                            return Promise.reject(
                              new Error(intl.get('reservation_error_channelNo_format')),
                            );
                          }
                          return Promise.resolve('');
                        },
                      },
                    ]}
                  >
                    <Input placeholder={intl.get('bill_credit_input_channel_bill_no')} />
                  </Form.Item>
                </Form>
              </dd>
            </dl>
          ) : (
            <dl className={styles['credit-pay']}>
              <dt className={commonStyles['bill-block-title']}>
                {intl.get('bill_credit_cashier')}
              </dt>
              <dd className={styles['credit-pay-item']}>
                <label className="text-colon">{intl.get('bill_credit_account_receivable')}</label>
                <span className="amount-pay">{parseFen2Currency(amount)}</span>
              </dd>
              <dd>
                <Payment
                  isInitPayTypes={initPayTypes}
                  selectPayType={(type) => setPayType(type)}
                  resvNo={resvNo}
                  setIdCardNo={(no) => setCertNo(no)}
                  setWyuid={(uid) => setWyuid(uid)}
                />
              </dd>
            </dl>
          )}
        </TabPane>
        <TabPane tab={intl.get('bill_consume')} key="2" forceRender>
          <dl className={styles['credit-block']}>
            <dt className={commonStyles['bill-block-title']}>
              {intl.get('bill_credit_consume_project')}
            </dt>
            <dd>
              <Form
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 8 }}
                form={consumeForm}
                preserve={false}
                className={styles['credit-form']}
              >
                <Form.Item label={intl.get('bill_refound_res_bill_subject')} name="subjectCode">
                  <Select
                    placeholder={intl.get('bill_credit_please_select')}
                    options={consumeSubjects}
                  />
                </Form.Item>
                <Form.Item
                  label={intl.get('bill_credit_amount')}
                  name="amount"
                  rules={[
                    {
                      validator: (_, value) => {
                        return validateAmount(value, {
                          required: true,
                        });
                      },
                    },
                  ]}
                >
                  <Input
                    prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                    placeholder={intl.get('bill_credit_input_consume_amount')}
                    onBlur={(e) => {
                      const v = formatAmount(e.target.value);
                      consumeForm.setFieldsValue({
                        amount: v,
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item label={intl.get('bill_remark')} name="remark">
                  <TextArea
                    showCount
                    rows={4}
                    maxLength={10000}
                    placeholder={intl.get('bill_credit_account_remark_placeholder')}
                  />
                </Form.Item>
              </Form>
            </dd>
          </dl>
        </TabPane>
      </>
    );
  };

  return (
    <Drawer
      width="50%"
      open={drawerVisible}
      title={intl.get('bill_credit_account')}
      maskClosable={false}
      closable={false}
      destroyOnClose
      forceRender
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <PayModal
        ref={payModalRef}
        payResultCallback={(payResult) => payResultCallback(payResult)}
        onConfirmOk={() => gotoPay(true)}
      />
      <Tabs
        activeKey={activeKey}
        type="card"
        className={styles['credit-tab']}
        onChange={(key) => setActiveKey(key)}
      >
        {renderTabContent()}
      </Tabs>
    </Drawer>
  );
}

const mapStateToProps = (state) => {
  const { paymentSubjects, consumeSubjects, selectedRoom } = state.bill;
  const { drawerVisible, allOta } = state.credit;
  return {
    paymentSubjects,
    consumeSubjects,
    selectedRoom,
    drawerVisible,
    allOta,
  };
};

export default connect(mapStateToProps, {
  toggleCreditDrawerVisible,
  initAllOta,
  initConsumeSubjects,
  initPaymentSubjects,
})(Credit);
